import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import HeaderLogoView from '@/website/components/logo/headerLogo';
import { localeToLanguage } from '@/website/constants/locales';
import { useLocaleContext } from '@/website/contexts/AppContext';
import { useInternationalizationLanguage } from '@/website/hooks/internationalization/useInternationalizationLanguage';
import { useQuoteReleatedQuote } from '@/website/hooks/useQuoteReleatedQuote';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { handleQuoteMessage } from '@/website/util/iframe';
import { getInitialWebsiteLanguage } from '@/website/util/language';
import { goToWebsiteHomePage } from '@/website/util/routes';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import {
  AmericanCountryList,
  JoveInternationalizationCountrySwitchView,
  JoveInternationalizationLanguageSwitchView,
  JoveLanguageEnum,
  PromotionEntityType,
  PromotionGetResponse,
  Regions,
  UKCountryList,
  getMainLanguageByCountryCode,
  onlyUKCountryList,
  useCountryStore,
} from '@jove-frontend/uikit';
import { cloneDeep, isArray } from 'lodash';
import { lintrk } from 'nextjs-linkedin-insight-tag';
import { useAnalytics } from 'use-analytics';
import { LanguageSwitcherDynamicParamsInterface } from '../Locale/LanguageSwitcher';
import WidgetEmbed from '../WidgetEmbed';
import { CompanyToggleButtons } from './CompanyToggleButtons';
import { GetQuoteButton } from './GetQuoteButton';
import JoveCommunityBar from './JoveCommunityBar';
import { Logo } from './Logo';
import { NavBar } from './NavBar';
import { ProductsBar, ProductsBarMobile } from './ProductsBar';
import { UsecasesBar, UsecasesBarMobile } from './UsecasesBar';

const LinkedinConversionId = process.env.NX_LINKEDIN_CONVERSION_ID;

type Props = {
  className?: string;
  customLogoUrl?: string;
  promotion?: PromotionGetResponse;
} & LanguageSwitcherDynamicParamsInterface;

export function Header(props: Props) {
  const { className, customLogoUrl, promotion, insuranceType, productScheme } = props;
  const router = useRouter();
  const { track } = useAnalytics();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isProductsBarOpen, setIsProductsBarOpen] = useState<boolean>(false);
  const [isUsecasesBarOpen, setIsUsecasesBarOpen] = useState<boolean>(false);
  const [isJoveCommunityBarOpen, setIsJoveCommunityBarOpen] = useState<boolean>(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState<boolean>(false);
  const [quoteTheme, setQuoteTheme] = useState<any>(
    router.pathname === '/landing/studiospace' ? 'studiospace' : router.pathname === '/landing/wamo' ? 'wamo' : 'default'
  );
  const [isFromLandingLink] = useState(!!router.query['promotionSlug']);
  const {
    state: { promotionInfo },
  } = promotionInfoStore();
  const { jumpToQuotePageAction } = useQuoteReleatedQuote();
  const {
    actions: { fetchCountries },
  } = useCountryStore();
  const { country, setGlobalCountry, language, setLanguage } = useLocaleContext();

  const {
    selectCountry,
    setSelectCountry,
    canSelectCountryList,
    selectLanguage,
    setSelectLanguage,
    canSelectLanguageList,
    selectCountryChangeAction,
    selectLanguageChangeAction,
  } = useInternationalizationLanguage({ country, language: getInitialWebsiteLanguage(router.locale, language) });
  const [countrySwitchCanSelectCountryList, setCountrySwitchCanSelectCountryList] = useState([]);
  const [languageSwitchCanSelectLanguageList, setLanguageSwitchCanSelectLanguageList] = useState([]);

  /**
   * effects
   */
  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    let list = cloneDeep(canSelectCountryList);
    const { promotion_type, regions, countries } = (promotionInfo || {}) as any;
    if ([PromotionEntityType.BROKER_PROMOTION, PromotionEntityType.PARTNER_PROMOTION].includes(promotion_type) && regions) {
      list = [];
      if (regions.includes(Regions.GB)) {
        onlyUKCountryList.forEach((code) => {
          const temp = canSelectCountryList?.find((aItem) => aItem.value === code);
          if (temp) {
            list.push(temp);
          }
        });
      }
      if (regions.includes(Regions.US)) {
        AmericanCountryList.forEach((code) => {
          const temp = canSelectCountryList?.find((aItem) => aItem.value === code);
          if (temp) {
            list.push(temp);
          }
        });
      }
      if (regions.includes(Regions.EU)) {
        (countries || []).forEach((item) => {
          const temp = canSelectCountryList?.find((aItem) => aItem.value === item.code);
          if (temp) {
            list.push(temp);
          }
        });
      }
    }
    setCountrySwitchCanSelectCountryList(list);
    if (isArray(list) && list.length > 0 && !list.find((item) => item?.value === country)) {
      try {
        setGlobalCountry(list[0].value);
        setSelectCountry(list[0].value);
      } catch (error) {
        console.log('set country error: ', error, list);
      }
    }
  }, [canSelectCountryList, promotionInfo]);

  useEffect(() => {
    const list = [];
    canSelectLanguageList.forEach((item) => {
      if ([JoveLanguageEnum.EN].includes(item.value) || selectCountry === item.country) {
        list.push({ ...item });
      }
    });
    if (list?.length > 0 && !list.find((item) => item.value === selectLanguage)) {
      setLanguage(list[0].value);
      setSelectLanguage(list[0].value);
    }
    setLanguageSwitchCanSelectLanguageList(list);
  }, [selectCountry, canSelectLanguageList]);

  const toggleMobileNavigation = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleProductsBar = () =>
    setIsProductsBarOpen(() => {
      if (isUsecasesBarOpen) {
        setIsUsecasesBarOpen(false);
      }
      return !isProductsBarOpen;
    });
  const toggleUsecasesBar = () =>
    setIsUsecasesBarOpen(() => {
      if (isProductsBarOpen) {
        setIsProductsBarOpen(false);
      }
      return !isUsecasesBarOpen;
    });
  const toggleJoveCommunityBar = () =>
    setIsJoveCommunityBarOpen(() => {
      if (isJoveCommunityBarOpen) {
        setIsJoveCommunityBarOpen(false);
      }
      return !isJoveCommunityBarOpen;
    });
  const toggleQuoteModal = () => setIsQuoteModalOpen(true);

  useEffect(() => {
    const listener = (e: MessageEvent<string>) => {
      if (e.data === 'close-iframe') {
        setIsQuoteModalOpen(false);
      } else {
        handleQuoteMessage(e.data);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('message', listener);
    }
    return () => window.removeEventListener('message', listener);
  });

  // stop scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.getElementsByTagName('html')[0].classList.add('overflow-hidden');
    } else {
      document.getElementsByTagName('html')[0].classList.remove('overflow-hidden');
    }
  });

  // each time modal is opened, track it
  useEffect(() => {
    if (isQuoteModalOpen) {
      track('open_quote_modal');
      track('click_get_quote_button');
      lintrk('track', LinkedinConversionId);
    }
  }, [isQuoteModalOpen]);

  /**
   * methods
   */
  const openQuotePageAction = () => {
    jumpToQuotePageAction({ theme: quoteTheme });
  };

  const goHomeAction = () => {
    goToWebsiteHomePage(router);
  };

  // Class names for color modfiers based on branding
  const mobileMenuBg = 'bg-black/75';
  const mobileMenuButtonColor = 'text-white';

  // set customBrand to 'studiospace' if on studiospace page or 'wamo' if on wamo page
  const customBrand = router.pathname === '/landing/studiospace' ? 'studiospace' : router.pathname === '/landing/wamo' ? 'wamo' : null;

  return (
    <>
      <div className={`header sticky top-0 z-40 hidden w-full bg-white p-2 shadow-md lg:block lg:p-0 ${className}`}>
        <div className="mx-auto flex w-full max-w-[96%] items-center justify-between px-2 py-2 xl:px-6 xl:py-6">
          <div className="flex space-x-4">
            {customLogoUrl ? (
              <HeaderLogoView hideJoveLogo={!!promotion} customLogoUrl={customLogoUrl} className="flex-row items-center space-x-2" />
            ) : (
              <div className="cursor-pointer flex-row items-center" onClick={goHomeAction}>
                <Logo organizationSlug={router.query['promotionSlug'] as string} className="" orgLogoClassName="h-[30px] w-auto" />
              </div>
            )}

            {!customBrand && !isFromLandingLink && (
              <nav className="flex items-center space-x-6">
                <CompanyToggleButtons />
              </nav>
            )}
          </div>

          <NavBar
            productsBarOpen={isProductsBarOpen}
            usecasesBarOpen={isUsecasesBarOpen}
            joveCommunityBarOpen={isJoveCommunityBarOpen}
            openQuoteModal={toggleQuoteModal}
            toggleProductsBar={toggleProductsBar}
            toggleUsecasesBar={toggleUsecasesBar}
            toggleJoveCommunityBar={toggleJoveCommunityBar}
            closeSubMenus={() => {
              setIsProductsBarOpen(false);
              setIsUsecasesBarOpen(false);
            }}
            promotion={promotion}
            insuranceType={insuranceType}
            productScheme={productScheme}
            countrySwitchCanSelectCountryList={countrySwitchCanSelectCountryList}
            selectCountry={selectCountry}
            selectCountryChange={selectCountryChangeAction}
            languageSwitchCanSelectLanguageList={languageSwitchCanSelectLanguageList}
            selectLanguage={selectLanguage}
            selectLanguageChange={selectLanguageChangeAction}
          />
        </div>
        <ProductsBar isOpen={isProductsBarOpen} />
        <UsecasesBar isOpen={isUsecasesBarOpen} />
        <JoveCommunityBar isOpen={isJoveCommunityBarOpen} />
      </div>

      {/* mobile nav start */}
      <div className={`${mobileMenuBg} flex flex-row items-center justify-between p-6 lg:hidden`}>
        <div className="flex flex-col space-y-3">
          <div className="flex space-x-4">
            {customLogoUrl ? (
              <HeaderLogoView hideJoveLogo={!!promotion} customLogoUrl={customLogoUrl} className="flex-row items-center space-x-2" />
            ) : (
              <Link href="/personal/business-insurance/" aria-label="logo" className="flex flex-row items-center">
                <Logo
                  organizationSlug={router.query['promotionSlug'] as string}
                  className="flex-row items-center space-x-1"
                  orgLogoClassName="h-[30px] w-auto"
                />
              </Link>
            )}
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            {/* <LocaleSwitcher isMobile={true} promotion={promotionInfo} />
            <LanguageSwitcher isMobile={true} insuranceType={insuranceType} productScheme={productScheme} /> */}
            <JoveInternationalizationCountrySwitchView
              countryList={countrySwitchCanSelectCountryList}
              value={selectCountry}
              valueChange={selectCountryChangeAction}
              promotionInfo={promotionInfo}
            />
            {![...AmericanCountryList, ...UKCountryList].includes(selectCountry) && (
              <JoveInternationalizationLanguageSwitchView
                languageList={languageSwitchCanSelectLanguageList}
                value={selectLanguage}
                valueChange={selectLanguageChangeAction}
                textColor="#fff"
              />
            )}
          </div>
        </div>
        <div className="flex flex-row justify-end space-x-2">
          <button onClick={toggleMobileNavigation}>
            <MenuIcon className={`${mobileMenuButtonColor} h-8`} />
          </button>
        </div>
      </div>
      {/* mobile nav end */}

      {/* mobile menu start */}
      {isMobileMenuOpen && (
        <div
          className={`${mobileMenuBg} fixed inset-0 z-40 h-screen w-screen space-y-8 overflow-auto p-6 pb-20 backdrop-blur-md backdrop-filter lg:hidden`}
        >
          <div className="flex items-center justify-between">
            <Link href="/personal/business-insurance/" aria-label="logo">
              <Logo organizationSlug={router.query['promotionSlug'] as string} />
            </Link>
            <button onClick={toggleMobileNavigation}>
              <XIcon className={`${mobileMenuButtonColor} w-8`} />
            </button>
          </div>
          {!customBrand && <div className="py-4">{<CompanyToggleButtons />}</div>}
          {router.asPath.startsWith('/personal') && <ProductsBarMobile />}
          {!router.asPath.startsWith('/personal') && !customBrand && <UsecasesBarMobile />}
          <NavBar
            isMobile={true}
            productsBarOpen={isProductsBarOpen}
            usecasesBarOpen={isUsecasesBarOpen}
            openQuoteModal={toggleQuoteModal}
            toggleProductsBar={toggleProductsBar}
            toggleUsecasesBar={toggleUsecasesBar}
            closeSubMenus={() => {
              setIsProductsBarOpen(false);
              setIsUsecasesBarOpen(false);
            }}
            countrySwitchCanSelectCountryList={countrySwitchCanSelectCountryList}
            selectCountry={selectCountry}
            selectCountryChange={selectCountryChangeAction}
            languageSwitchCanSelectLanguageList={languageSwitchCanSelectLanguageList}
            selectLanguage={selectLanguage}
            selectLanguageChange={selectLanguageChangeAction}
          />
          {router.asPath.startsWith('/personal') && (
            <div className="flex items-center justify-center space-x-4">
              <GetQuoteButton
                type="secondary"
                openQuoteModal={openQuotePageAction}
                // onClick={toggleMobileNavigation}
              />
            </div>
          )}
        </div>
      )}
      {/* mobile menu end */}
      <WidgetEmbed isOpen={isQuoteModalOpen} theme={quoteTheme} />
    </>
  );
}

export default Header;
